/**
 * Browser-side `go_to_position`: view-only, auto-run like a read. Moves the
 * viewer along the patient's head–feet axis, like scrolling:
 *  - structure + top/middle/bottom: the first/last slice that contains the
 *    structure's contour (top = towards the head, i.e. the larger patient Z,
 *    whatever the scan direction), or halfway between;
 *  - zCm: the Z position in cm exactly as the viewer shows it (patient Z in
 *    mm ÷ 10). The viewer shows no slice numbers, so none are accepted.
 * The point always snaps to the nearest image slice; a position outside the
 * scan is refused, never clamped. Ports are injected (React-free).
 */
import { resolveRoi, isPointRoiType } from '../core/roiResolver';
import { asRecord, toListed, type StructureFacts } from '../core';
import type { GoToPositionOutput } from '../../contract';

/** A patient-space point in mm: [x, y, z], z towards the head. */
export type PatientPoint = readonly [number, number, number];

export interface StructureEnds {
    readonly top: PatientPoint;
    readonly middle: PatientPoint;
    readonly bottom: PatientPoint;
}

export interface GoToPositionDeps {
    readonly viewReady: boolean;
    readonly structures: ReadonlyArray<StructureFacts>;
    /** Slice centres of a structure's contour extent; undefined if none. */
    structureEnds(roiNumber: number): StructureEnds | undefined;
    /** The point the views currently show. */
    currentPoint(): PatientPoint;
    /** Snap to the nearest image slice centre; undefined = outside scan. */
    snapToSlice(point: PatientPoint): PatientPoint | undefined;
    /** The scan's Z range in cm, for the refusal message. */
    scanRangeCm(): { readonly fromCm: number; readonly toCm: number };
    /** Move the views there (the scroll action's call). */
    moveTo(point: PatientPoint): void;
}

const WHERE = new Set(['top', 'middle', 'bottom']);
/** Same rounding as the viewer's Z label (cm, 2 decimals). */
const toCm = (mm: number) => Math.round(mm * 10) / 100;

export function goToPosition(
    input: unknown,
    deps: GoToPositionDeps
): GoToPositionOutput {
    if (!deps.viewReady) {
        return {
            status: 'unavailable',
            reason: 'no image is shown in the viewer',
        };
    }
    const o = asRecord(input) ?? {};
    const { roi, where, zCm } = o;

    if (typeof zCm === 'number') {
        if (!Number.isFinite(zCm) || roi !== undefined) {
            return {
                status: 'unavailable',
                reason: 'give either a Z position in cm or a structure, not both',
            };
        }
        const [x, y] = deps.currentPoint();
        const snapped = deps.snapToSlice([x, y, zCm * 10]);
        if (!snapped) {
            const { fromCm, toCm: endCm } = deps.scanRangeCm();
            return {
                status: 'unavailable',
                reason:
                    `Z ${zCm} cm is outside the scan ` +
                    `(Z ${fromCm.toFixed(2)} to ${endCm.toFixed(2)} cm)`,
            };
        }
        deps.moveTo(snapped);
        return { status: 'ok', zCm: toCm(snapped[2]) };
    }

    if (typeof roi !== 'string' || !roi) {
        return {
            status: 'unavailable',
            reason:
                'name a structure with top/middle/bottom, or a Z position in ' +
                'cm (the viewer has no slice numbers)',
        };
    }
    const position =
        typeof where === 'string' && WHERE.has(where) ? where : 'middle';
    const resolution = resolveRoi(roi, deps.structures.map(toListed));
    if (resolution.kind === 'ambiguous') {
        return {
            status: 'ambiguous',
            reason: resolution.reason,
            candidates: resolution.candidates.map((c) => ({
                roiNumber: c.roiNumber,
                name: c.name,
            })),
        };
    }
    if (resolution.kind === 'not-found') {
        return {
            status: 'not-found',
            suggestions: resolution.suggestions.map((s) => ({
                roiNumber: s.roiNumber,
                name: s.name,
            })),
        };
    }
    const target = deps.structures.find(
        (s) => s.roiNumber === resolution.structure.roiNumber
    );
    if (!target) {
        return {
            status: 'unavailable',
            reason: 'resolved structure not found',
        };
    }
    if (isPointRoiType(target.type)) {
        return {
            status: 'unavailable',
            reason: `'${target.name}' is a point ROI`,
        };
    }
    const ends = target.isEmpty
        ? undefined
        : deps.structureEnds(target.roiNumber);
    if (!ends) {
        return {
            status: 'unavailable',
            reason: `'${target.name}' has no contours to go to`,
        };
    }
    const point = deps.snapToSlice(ends[position as keyof StructureEnds]);
    if (!point) {
        return {
            status: 'unavailable',
            reason: `'${target.name}' lies outside the scan`,
        };
    }
    deps.moveTo(point);
    return {
        status: 'ok',
        zCm: toCm(point[2]),
        structure: { roiNumber: target.roiNumber, name: target.name },
        where: position as 'top' | 'middle' | 'bottom',
    };
}
